/**
 * Parser for HWiNFO's CSV logging output — the file "Start Logging" writes, one
 * row per polling interval, not the point-in-time "Save Report".
 *
 * Everything here is defensive for two reasons that bite in practice:
 *
 * 1. **Columns depend on the hardware.** An Intel board logs `CPU Package`, an
 *    AMD one `CPU (Tctl/Tdie)`; a card with no hotspot sensor simply has no
 *    hotspot column. So sensors are matched by name fragment in preference
 *    order, never by position, and anything not found stays null. A missing
 *    sensor must read as "not measured" and never as zero — a 0 °C max would
 *    look like a spectacularly cool PC.
 *
 * 2. **The file follows the Windows locale.** HWiNFO writes with the system
 *    list separator, so a machine set to a comma-decimal locale produces a
 *    semicolon-separated file with `45,5` in it. Both are detected.
 *
 * The degree sign is deliberately never matched on: it arrives as U+00B0 from a
 * UTF-8 file and as a replacement character from a Windows-1252 one, so unit
 * suffixes are stripped before comparison rather than relied upon.
 */

/** One sensor column that survived matching. */
interface Column {
  index: number;
  header: string;
}

export interface HwinfoMetrics {
  sampleCount: number;
  durationSeconds: number | null;
  startedAt: string | null;
  /** True when the log's date could be read either way and day-first was
   *  assumed — see parseLogDate. */
  dateAmbiguous: boolean;

  cpuTempMax: number | null;
  cpuTempAvg: number | null;
  gpuTempMax: number | null;
  gpuTempAvg: number | null;
  gpuHotspotMax: number | null;
  cpuClockMin: number | null;
  cpuClockMax: number | null;
  gpuClockMin: number | null;
  gpuClockMax: number | null;
  gpuPowerMax: number | null;
  fanRpmMax: number | null;

  /** Null when the log carried no *thermal* throttling column at all, which is
   *  not the same as "did not throttle". */
  thermalThrottled: boolean | null;
  /** Non-thermal limits that fired during the run, by column name - a GPU on
   *  its power limit is normal under load, so it is reported, not flagged. */
  otherThrottleReasons: string[];

  /** metric key -> the column header it was read from, so a surprising figure
   *  can be traced back to the sensor that produced it. */
  matchedSensors: Record<string, string>;
  /** Metrics we looked for and did not find in this log. */
  unmatched: string[];
}

export interface HwinfoParseResult {
  metrics: HwinfoMetrics;
  /** Populated when the file could not be read as an HWiNFO log at all. */
  error: string | null;
}

/**
 * Split `CPU (Tctl/Tdie) [°C]` into a comparable name and its unit.
 *
 * Keeping the unit is what makes matching safe. Name fragments alone are not
 * enough: `CPU Package Power [W]` contains "cpupackage", so a temperature rule
 * looking only at names happily reports watts as degrees. Requiring the unit to
 * be a temperature closes that off, and it survives the encoding problem —
 * HWiNFO writes Windows-1252, so the degree sign arrives as a replacement
 * character, but the `C` beside it does not.
 */
function splitHeader(h: string): { name: string; unit: string } {
  const s = String(h ?? '').trim();
  const m = s.match(/\[([^\]]*)\]\s*$/);
  const unit = m ? m[1].toLowerCase().replace(/[^a-z0-9/]/g, '') : '';
  const name = (m ? s.slice(0, m.index) : s).toLowerCase().replace(/[^a-z0-9]/g, '');
  return { name, unit };
}

/** Just the comparable name, for the few places the unit is irrelevant. */
function normaliseHeader(h: string): string {
  return splitHeader(h).name;
}

/**
 * Split one CSV line on `delim`, honouring double-quoted fields. HWiNFO quotes
 * any sensor name containing the separator, so a naive split corrupts the
 * header row on some boards.
 */
function splitLine(line: string, delim: string): string[] {
  const out: string[] = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (quoted && line[i + 1] === '"') { cur += '"'; i++; }
      else quoted = !quoted;
    } else if (c === delim && !quoted) {
      out.push(cur); cur = '';
    } else {
      cur += c;
    }
  }
  out.push(cur);
  return out;
}

/** Whichever of , or ; appears more often outside quotes in the header. */
function detectDelimiter(headerLine: string): string {
  const count = (d: string) => splitLine(headerLine, d).length;
  return count(';') > count(',') ? ';' : ',';
}

/**
 * A numeric cell. Handles both decimal conventions: a comma-decimal locale
 * writes `45,5`, and since that file is semicolon-separated the comma is
 * unambiguously a decimal point by the time we get here. Thousands separators
 * do not appear in HWiNFO's numeric output.
 */
function num(raw: string): number | null {
  const s = String(raw ?? '').trim();
  if (!s || /^(n\/?a|-|—)$/i.test(s)) return null;
  const n = parseFloat(s.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/** HWiNFO writes Yes/No for throttling columns; some builds write 1/0. */
function isYes(raw: string): boolean {
  const s = String(raw ?? '').trim().toLowerCase();
  return s === 'yes' || s === 'true' || s === '1';
}

/**
 * `HH:MM:SS` or `HH:MM:SS.mmm` to seconds past midnight. HWiNFO's Time column
 * is locale-independent, which is why duration is derived from it rather than
 * from the Date column.
 */
function timeToSeconds(raw: string): number | null {
  // Minutes and seconds are NOT zero-padded - a real log carries `6:22:1.967`
  // alongside `6:22:12.005`. Demanding two digits silently dropped every row
  // whose second fell under ten, which is a tenth of every log.
  const m = String(raw ?? '').trim().match(/^(\d{1,2}):(\d{1,2}):(\d{1,2})(?:[.,](\d+))?$/);
  if (!m) return null;
  const [, h, mm, ss, frac] = m;
  return Number(h) * 3600 + Number(mm) * 60 + Number(ss) + (frac ? Number(`0.${frac}`) : 0);
}

/**
 * HWiNFO's Date column follows the Windows locale, so `9.7.2026` is 9 July on
 * these machines and 9 September on a US-configured one, with nothing in the
 * file to say which.
 *
 * Day-first is assumed, because that is what the machines producing these logs
 * are set to, and `ambiguous` is reported alongside so a wrong reading is
 * visible rather than silent. This is not the sales importer's mistake in
 * reverse: there, a guess moved revenue between months on a report people act
 * on; here the worst case is a start time that is off, next to a created_at
 * that is not.
 */
function parseLogDate(raw: string, timeSeconds: number | null): { iso: string | null; ambiguous: boolean } {
  const m = String(raw ?? '').trim().match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/);
  if (!m) return { iso: null, ambiguous: false };
  const [, a, b, y] = m;
  let year = Number(y);
  if (year < 100) year += 2000;

  let day: number, month: number, ambiguous = false;
  if (Number(a) > 12 && Number(b) <= 12) { day = Number(a); month = Number(b); }
  else if (Number(b) > 12 && Number(a) <= 12) { month = Number(a); day = Number(b); }
  else { day = Number(a); month = Number(b); ambiguous = true; } // day-first

  if (month < 1 || month > 12 || day < 1 || day > 31) return { iso: null, ambiguous: false };
  const secs = timeSeconds ?? 0;
  const hh = String(Math.floor(secs / 3600)).padStart(2, '0');
  const mm = String(Math.floor((secs % 3600) / 60)).padStart(2, '0');
  const ss = String(Math.floor(secs % 60)).padStart(2, '0');
  return {
    iso: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${hh}:${mm}:${ss}`,
    ambiguous,
  };
}

/**
 * What each metric looks like: an acceptable unit, name fragments in preference
 * order, and fragments that disqualify a column.
 *
 * The unit does the heavy lifting. Without it, `cpuTemp` matched `CPU Package
 * Power [W]` on a real log and reported 27.8 W as 27.8 °C - a number that looks
 * entirely plausible and is entirely wrong.
 *
 * The exclusions still matter within a unit: `GPU Memory Temperature` and `GPU
 * Hot Spot Temperature` are both °C and both contain "gputemperature" once
 * normalised, so without them the core GPU temperature is whichever came first.
 *
 * `multi` takes the maximum across every matching column rather than the first.
 * A machine has several fans - case, CPU, GPU - and the highest is the only
 * interesting one.
 */
interface SensorSpec {
  /** Normalised unit the column must carry. Temperatures accept C or F. */
  unit: string[];
  any: string[];
  not?: string[];
  multi?: boolean;
}

const TEMP = ['c', 'f'];

const SENSORS: Record<string, SensorSpec> = {
  cpuTemp: {
    unit: TEMP,
    any: ['cpupackage', 'cputctltdie', 'tctltdie', 'coretemperatures', 'cpudie', 'coremax', 'cputemperature'],
    not: ['motherboard', 'vrm', 'distancetotjmax', 'ccd'],
  },
  gpuTemp: {
    unit: TEMP,
    any: ['gputemperature', 'gpucoretemperature'],
    not: ['hotspot', 'memory', 'vrm'],
  },
  gpuHotspot: {
    unit: TEMP,
    any: ['gpuhotspot', 'gputemperaturehotspot'],
  },
  cpuClock: {
    unit: ['mhz'],
    any: ['coreclocks', 'cpuclock'],
    not: ['ring', 'uncore', 'memory', 'gpu', 'effective', 'bus'],
  },
  gpuClock: {
    unit: ['mhz'],
    any: ['gpuclock', 'gpucoreclock'],
    not: ['memory', 'video', 'effective'],
  },
  gpuPower: {
    unit: ['w'],
    // NVIDIA reports board power as `GPU Power`; AMD has no such column and
    // `GPU ASIC Power` is the nearest whole-card figure. Core/SoC rails are
    // last resorts - they measure a part, not the card.
    any: ['gpupower', 'totalgraphicspower', 'gpurailpower', 'gpuboardpower', 'gpuasicpower', 'gpucorepower'],
    not: ['limit', 'percent', 'soc'],
  },
  fanRpm: {
    unit: ['rpm'],
    any: ['fan'],
    not: ['percent', 'duty', 'pwm'],
    multi: true,
  },
};

/** Every column matching a spec, in header order, best fragment first. */
function findColumns(headers: string[], spec: SensorSpec): Column[] {
  const parsed = headers.map(splitHeader);
  const ok = (i: number, frag: string) => {
    const { name, unit } = parsed[i];
    if (!spec.unit.includes(unit)) return false;
    if (!name.includes(frag)) return false;
    if (spec.not?.some(n => name.includes(n))) return false;
    return true;
  };
  if (spec.multi) {
    const out: Column[] = [];
    for (let i = 0; i < headers.length; i++) {
      if (spec.any.some(f => ok(i, f))) out.push({ index: i, header: headers[i] });
    }
    return out;
  }
  for (const frag of spec.any) {
    for (let i = 0; i < headers.length; i++) {
      if (ok(i, frag)) return [{ index: i, header: headers[i] }];
    }
  }
  return [];
}

/** Running min/max/mean over one column, ignoring blanks and N/A. */
class Series {
  min: number | null = null;
  max: number | null = null;
  private sum = 0;
  private n = 0;
  add(v: number | null) {
    if (v === null) return;
    if (this.min === null || v < this.min) this.min = v;
    if (this.max === null || v > this.max) this.max = v;
    this.sum += v; this.n++;
  }
  get avg(): number | null {
    return this.n === 0 ? null : Math.round((this.sum / this.n) * 10) / 10;
  }
}

const round1 = (n: number | null): number | null => (n === null ? null : Math.round(n * 10) / 10);
const asInt = (n: number | null): number | null => (n === null ? null : Math.round(n));

/**
 * Parse an HWiNFO CSV log into the metrics worth keeping.
 *
 * Reads the whole file into memory, which is fine for the shape these logs
 * take — a twenty-minute run at two-second intervals is a few hundred rows.
 */
export async function parseHwinfoLog(file: File): Promise<HwinfoParseResult> {
  const text = await file.text();
  return parseHwinfoText(text);
}

/** The pure half, so it can be tested without a File. */
export function parseHwinfoText(text: string): HwinfoParseResult {
  const empty: HwinfoMetrics = {
    sampleCount: 0, durationSeconds: null, startedAt: null, dateAmbiguous: false,
    cpuTempMax: null, cpuTempAvg: null, gpuTempMax: null, gpuTempAvg: null,
    gpuHotspotMax: null, cpuClockMin: null, cpuClockMax: null,
    gpuClockMin: null, gpuClockMax: null, gpuPowerMax: null, fanRpmMax: null,
    thermalThrottled: null, otherThrottleReasons: [],
    matchedSensors: {}, unmatched: Object.keys(SENSORS),
  };

  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter(l => l.trim() !== '');
  if (lines.length < 2) {
    return { metrics: empty, error: 'File has no data rows — is this an HWiNFO CSV log?' };
  }

  const delim = detectDelimiter(lines[0]);
  const headers = splitLine(lines[0], delim).map(h => h.trim().replace(/^"|"$/g, ''));
  if (headers.length < 3) {
    return { metrics: empty, error: 'Could not read a header row — is this an HWiNFO CSV log?' };
  }

  const normalised = headers.map(normaliseHeader);
  const dateIdx = normalised.findIndex(h => h === 'date');
  const timeIdx = normalised.findIndex(h => h === 'time');

  // Throttling columns, split by what they actually mean. A GPU sitting on its
  // power limit under load is normal and expected - counting `Throttle Reason -
  // Power` as thermal throttling would flag virtually every healthy stress test.
  // Only genuinely thermal signals set thermal_throttled; the rest are recorded
  // by name so the full picture survives.
  const throttleCols = normalised
    .map((h, i) => ({ h, i }))
    .filter(({ h }) => h.includes('throttl') || h.includes('perflimit'));
  const isThermal = (h: string) => h.includes('thermal') || h.includes('prochot') || h.includes('htc');
  const thermalIdx = throttleCols.filter(({ h }) => isThermal(h)).map(({ i }) => i);
  const otherIdx = throttleCols.filter(({ h }) => !isThermal(h)).map(({ i }) => i);
  const otherFired = new Set<string>();

  const cols: Record<string, Column[]> = {};
  for (const [key, spec] of Object.entries(SENSORS)) cols[key] = findColumns(headers, spec);

  const series: Record<string, Series> = {};
  for (const key of Object.keys(SENSORS)) series[key] = new Series();

  let sampleCount = 0;
  let firstTime: number | null = null;
  let lastTime: number | null = null;
  let firstDate = '';
  let throttled: boolean | null = thermalIdx.length > 0 ? false : null;

  for (let i = 1; i < lines.length; i++) {
    const row = splitLine(lines[i], delim);
    // HWiNFO closes a log with a summary/blank row that has far fewer fields.
    if (row.length < headers.length - 2) continue;

    const t = timeIdx >= 0 ? timeToSeconds(row[timeIdx]) : null;
    if (timeIdx >= 0 && t === null) continue; // not a data row

    sampleCount++;
    if (t !== null) {
      if (firstTime === null) { firstTime = t; if (dateIdx >= 0) firstDate = row[dateIdx]; }
      lastTime = t;
    }

    for (const key of Object.keys(SENSORS)) {
      for (const c of cols[key]) series[key].add(num(row[c.index]));
    }
    if (thermalIdx.length > 0 && thermalIdx.some(idx => isYes(row[idx]))) throttled = true;
    for (const idx of otherIdx) if (isYes(row[idx])) otherFired.add(headers[idx]);
  }

  if (sampleCount === 0) {
    return { metrics: empty, error: 'No readable data rows found in the log.' };
  }

  // Wrap past midnight rather than reporting a negative run.
  let durationSeconds: number | null = null;
  if (firstTime !== null && lastTime !== null) {
    let d = lastTime - firstTime;
    if (d < 0) d += 24 * 3600;
    durationSeconds = Math.round(d);
  }

  const logDate = parseLogDate(firstDate, firstTime);

  const matchedSensors: Record<string, string> = {};
  const unmatched: string[] = [];
  for (const [key, list] of Object.entries(cols)) {
    if (list.length > 0) matchedSensors[key] = list.map(c => c.header).join(', ');
    else unmatched.push(key);
  }

  return {
    error: null,
    metrics: {
      sampleCount,
      durationSeconds,
      startedAt: logDate.iso,
      dateAmbiguous: logDate.ambiguous,
      cpuTempMax: round1(series.cpuTemp.max),
      cpuTempAvg: series.cpuTemp.avg,
      gpuTempMax: round1(series.gpuTemp.max),
      gpuTempAvg: series.gpuTemp.avg,
      gpuHotspotMax: round1(series.gpuHotspot.max),
      cpuClockMin: asInt(series.cpuClock.min),
      cpuClockMax: asInt(series.cpuClock.max),
      gpuClockMin: asInt(series.gpuClock.min),
      gpuClockMax: asInt(series.gpuClock.max),
      gpuPowerMax: round1(series.gpuPower.max),
      fanRpmMax: asInt(series.fanRpm.max),
      thermalThrottled: throttled,
      otherThrottleReasons: [...otherFired],
      matchedSensors,
      unmatched,
    },
  };
}
